import { AutoComplete } from "antd";
import React, { useRef, useState, useMemo } from "react";

const Search = ({
  options = [], // Array of {label, value} objects OR grouped format [{label: "Group", options: [...]}]
  selectedOptions = [], // Array of {label, value} objects
  placeholder = "",
  label,
  count,
  onSelectedOptionsChange,
}) => {
  const searchInputRef = useRef(null);
  const [inputValue, setInputValue] = useState("");

  // Helper to check if options are grouped
  const isGroupedOptions = useMemo(() => {
    return options.length > 0 && options[0]?.options !== undefined;
  }, [options]);

  // Flatten grouped options for filtering
  const flattenedOptions = useMemo(() => {
    if (isGroupedOptions) {
      return options.flatMap((group) => group.options || []);
    }
    return options;
  }, [options, isGroupedOptions]);

  // Filter out already selected options from dropdown and apply search filter
  const availableOptions = useMemo(() => {
    const selectedValues = selectedOptions.map((opt) => opt.value);
    const searchTerm = inputValue.toLowerCase().trim();

    if (isGroupedOptions) {
      return options
        .map((group) => {
          // Filter options within each group
          const filteredGroupOptions = (group.options || []).filter((opt) => {
            // Exclude already selected options
            if (selectedValues.includes(opt.value)) {
              return false;
            }
            // Apply search filter if there's a search term
            if (searchTerm) {
              return opt.label?.toLowerCase().includes(searchTerm);
            }
            return true;
          });
          return {
            ...group,
            options: filteredGroupOptions,
          };
        })
        .filter((group) => group.options.length > 0); // Remove empty groups
    }

    return options.filter((opt) => {
      if (selectedValues.includes(opt.value)) {
        return false;
      }
      // Apply search filter if there's a search term
      if (searchTerm) {
        return opt.label?.toLowerCase().includes(searchTerm);
      }
      return true;
    });
  }, [options, selectedOptions, isGroupedOptions, inputValue]);

  const handleSelect = (value) => {
    // Find the full option object (works for both flat and grouped)
    const selectedOption = flattenedOptions.find((opt) => opt.value === value);
    if (!selectedOption) return;

    // Prevent duplicates
    if (selectedOptions.some((opt) => opt.value === value)) {
      searchInputRef.current?.focus();
      return;
    }

    onSelectedOptionsChange?.([...selectedOptions, selectedOption]);
    setInputValue("");
    searchInputRef.current?.focus();
  };

  const handleRemove = (optionToRemove) => {
    onSelectedOptionsChange?.(
      selectedOptions.filter((opt) => opt.value !== optionToRemove.value)
    );
  };

  const handleSearchBarClick = () => {
    searchInputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    // Backspace on empty input removes last tag
    if (e.key === "Backspace" && !inputValue && selectedOptions.length > 0) {
      handleRemove(selectedOptions[selectedOptions.length - 1]);
    }
  };

  return (
    <div>
      {label && (
        <label
          style={{
            display: "block",
            marginBottom: "8px",
            fontWeight: "500",
            fontSize: "14px",
          }}
        >
          {label}
        </label>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <div
          className="border"
          style={{
            cursor: "text",
            position: "relative",
            borderRadius: 6,
            backgroundColor: "#fff",
            transition: "all 0.3s ease",
            minHeight: "40px",
            width: "100%",
          }}
          onClick={handleSearchBarClick}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#40a9ff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#d9d9d9";
          }}
        >
          <div
            className="container"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "calc(100% - 80px)",
              flexWrap: "wrap",
              padding: "4px",
              minHeight: "32px",
              margin: 0,
            }}
          >
            {Array.isArray(selectedOptions) &&
              selectedOptions.length > 0 &&
              selectedOptions.map((option) => (
                <div
                  key={option.value}
                  style={{
                    width: "fit-content",
                    display: "flex",
                    alignItems: "center",
                    background: "var(--primary-background)",
                    color: "var(--primary-text-color)",
                    border: "none",
                    borderRadius: 4,
                    padding: "4px 8px",
                    margin: 3,
                    fontSize: "13px",
                    fontWeight: "500",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.2s ease",
                    animation: "slideIn 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 8px rgba(0, 0, 0, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 4px rgba(0, 0, 0, 0.1)";
                  }}
                >
                  <div style={{ whiteSpace: "nowrap" }}>{option.label}</div>
                  <button
                    type="button"
                    className="close"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(option);
                    }}
                    aria-label={`Remove ${option.label}`}
                    style={{
                      backgroundColor: "transparent",
                      border: "none",
                      color: "#fff",
                      marginLeft: 6,
                      padding: 0,
                      fontSize: "18px",
                      lineHeight: 1,
                      cursor: "pointer",
                      opacity: 0.8,
                      transition: "opacity 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = 1;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = 0.8;
                    }}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              ))}

            <div style={{ flex: 1, width: "100%" }}>
              <AutoComplete
                value={inputValue}
                onChange={setInputValue}
                options={availableOptions}
                onSelect={handleSelect}
                style={{ width: "100%" }}
                dropdownStyle={{
                  borderRadius: 6,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                }}
                popupClassName="search-autocomplete-dropdown"
                filterOption={false}
                showSearch={true}
              >
                <input
                  placeholder={placeholder}
                  className={`form-control form-control-sm`}
                  ref={searchInputRef}
                  onKeyDown={handleKeyDown}
                  style={{
                    outline: "none",
                    backgroundColor: "transparent",
                    border: "none",
                    borderRadius: 0,
                    width: "100%",
                    padding: "4px 8px",
                    fontSize: "14px",
                    boxShadow: "none",
                  }}
                  aria-label="Search input"
                />
              </AutoComplete>
            </div>
          </div>
          {options.length > 0 && count && (
            <div
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#666",
                backgroundColor: "#f5f5f5",
                height: "calc(100% - 8px)",
                padding: "0px 12px",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                borderRadius: 4,
                fontSize: "13px",
                fontWeight: "500",
                border: "1px solid #e8e8e8",
              }}
            >
              {count}
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        /* Remove blue highlight from autocomplete */
        .ant-select-focused .ant-select-selector,
        .ant-select-selector:focus,
        .ant-select-selector:active,
        .ant-select:focus .ant-select-selector,
        .form-control:focus {
          outline: none !important;
          box-shadow: none !important;
          border-color: transparent !important;
        }

        /* Style for grouped options */
        .search-autocomplete-dropdown .ant-select-item-group {
          color: #8c8c8c;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          padding: 8px 12px 4px;
          background-color: #fafafa;
          border-top: 1px solid #f0f0f0;
        }

        .search-autocomplete-dropdown .ant-select-item-group:first-child {
          border-top: none;
        }

        .search-autocomplete-dropdown .ant-select-item-option {
          padding: 8px 20px;
        }

        .search-autocomplete-dropdown .ant-select-item-option-grouped {
          padding-left: 24px;
        }
      `}</style>
    </div>
  );
};

export default Search;
